'use client';

import { useEffect, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { optInAnalytics, optOutAnalytics, pageView, identify, reset, setVisitorCountry } from '@/lib/analytics';
import { useAuth } from '@/context/auth-context';
import { useGeoCountry } from '@/context/geo-context';
import { CONSENT_CHANGED_EVENT, readConsent } from '@/lib/cookie-consent';

// Hands the server-resolved country to analytics (see setVisitorCountry). Held
// until PostHog starts, so nothing is sent before consent.
function CountryTracker() {
  const country = useGeoCountry();
  useEffect(() => {
    setVisitorCountry(country);
  }, [country]);
  return null;
}

function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname) return;
    const qs = searchParams?.toString();
    pageView(qs ? `${pathname}?${qs}` : pathname);
  }, [pathname, searchParams]);

  return null;
}

function IdentityTracker() {
  const { user, authUser, sellerTier } = useAuth();

  useEffect(() => {
    if (authUser?.uid) {
      identify(authUser.uid, {
        username: user?.username ?? null,
        sellerTier: sellerTier ?? null,
      });
    } else {
      reset();
    }
  }, [authUser?.uid, user?.username, sellerTier]);

  return null;
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  // Gate PostHog on the user's stored consent choice. Re-evaluates on the
  // `cookie-consent-changed` event so accepting/revoking via the banner takes
  // effect immediately without a reload.
  useEffect(() => {
    const apply = () => {
      const choice = readConsent();
      if (choice?.analytics) optInAnalytics();
      else optOutAnalytics();
    };
    apply();
    window.addEventListener(CONSENT_CHANGED_EVENT, apply);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, apply);
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PageViewTracker />
      </Suspense>
      <IdentityTracker />
      <CountryTracker />
      {children}
    </>
  );
}
